import classnames from 'classnames';
import { PASSWORD_MIN_LENGTH, RECAPTCHA_ENABLED } from 'config';
import { LOGIN, PRIVACY_POLICY, REFUND_POLICY, SIGNUP_SUCCESS, TERMS_OF_USE } from 'constants/routes';
import { isValidEmail } from 'helpers/mail';
import get from 'lodash/get';
import React from 'react';
import { RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import Services from 'services';
import { Country, CountryState } from 'types';

import Alert from 'components/Alert';
import Button from 'components/Button';
import Input from 'components/Input/Base';
import InputLabel from 'components/Input/Label';
import CountryAndStatePicker from 'components/Picker/CountryAndState';
import Recaptcha from 'components/Recaptcha';
import { Caption, Heading, Subtitle } from 'components/Text';
import { getTranslation, t } from 'components/Translate';

import styles from './Onboarding.module.scss';

type State = {
  email: string,
  password: string,
  country: Country | null,
  state: CountryState | null,
  isLoading: boolean,
  error: string | null,
  passwordAutofocus: boolean,
};

class Signup extends React.Component<RouteComponentProps, State> {
  private recaptcha = React.createRef<Recaptcha>();
  state: State = {
    email: this.email,
    password: '',
    country: null,
    state: null,
    isLoading: false,
    error: null,
    passwordAutofocus: this.locationHasEmail,
  };

  private get email() {
    return this.locationHasEmail ? this.props.location.state.email : '';
  }
  private get locationHasEmail() {
    const { location } = this.props;
    return location.state && location.state.email;
  }
  private get trackingId() {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    if (!window.mixpanel || !window.mixpanel.get_distinct_id) return undefined;

    return window.mixpanel.get_distinct_id();
  }
  private get hasStates() {
    return !!(this.state.country && !this.state.country.finalRegion);
  }
  private setCountry = (country: Country) => {
    this.setState({ country, state: null });
  };
  private setCountryState = (state: CountryState) => {
    this.setState({ state });
  };

  private onSubmit = async () => {
    if (!this.validate() || (RECAPTCHA_ENABLED && !this.recaptcha.current)) return;

    this.setState({ error: null, isLoading: true });
    try {
      let token;
      if (RECAPTCHA_ENABLED && this.recaptcha.current) {
        token = await this.recaptcha.current.execute();
      }
      await Services.get('auth').signUp({
        email: this.state.email,
        password: this.state.password,
        country: get(this.state.country, 'id', ''),
        state: get(this.state.state, 'id', null),
        token,
        trackingId: this.trackingId,
      });
      if (this.recaptcha.current) this.recaptcha.current.reset();
      this.props.history.push(SIGNUP_SUCCESS, { email: this.state.email });
    } catch (e) {
      this.setState({ error: e.message });
      if (this.recaptcha.current) this.recaptcha.current.reset();
    } finally {
      this.setState({ isLoading: false });
    }
  };

  private validate(): boolean {
    if (!this.state.email.length) {
      this.setState({ error: getTranslation('onboarding.error.emailRequired') });
      return false;
    }
    if (!isValidEmail(this.state.email)) {
      this.setState({ error: getTranslation('onboarding.error.emailInvalid') });
      return false;
    }
    if (this.state.password.length < PASSWORD_MIN_LENGTH) {
      this.setState({ error: getTranslation('onboarding.error.passwordLength', { length: PASSWORD_MIN_LENGTH }) });
      return false;
    }

    if (this.state.country === null || !this.state.country.id) {
      this.setState({ error: getTranslation('onboarding.error.countryRequired') });
      return false;
    }
    if (this.hasStates && !this.state.state) {
      this.setState({ error: getTranslation('onboarding.error.stateRequired') });
      return false;
    }

    if (RECAPTCHA_ENABLED && !this.recaptcha.current) {
      this.setState({ error: 'Recaptcha error!' });
      return false;
    }

    return true;
  }

  render() {
    return this.renderForm();
  }
  private renderForm() {
    const { passwordAutofocus } = this.state;

    return (
      <div className="t-center">
        <div className={ styles.signupContainer }>
          <div className={ styles.width }>
            <Heading type="large" className={ styles.title }>{ t('onboarding.signup.title') }</Heading>

            <InputLabel
              className={ classnames(styles.signupLabel, styles.firstInput, 'mt-0') }
              text={ t('onboarding.signup.label.email') }
            >
              <Input
                value={ this.state.email }
                onChange={ (email) => this.setState({ email }) }
                placeholder={ getTranslation('common.label.email') }
                fullWidth={ true }
                size="large"
                onEnter={ this.onSubmit }
                attrs={ {
                  autoFocus: !passwordAutofocus,
                  type: 'email',
                  formNoValidate: true,
                } }
              />
            </InputLabel>
            <InputLabel
              className={ styles.signupLabel }
              text={ t('onboarding.signup.label.password') }
            >
              <Input
                className={ styles.passwordInput }
                value={ this.state.password }
                onChange={ (password) => this.setState({ password }) }
                placeholder={ getTranslation('common.label.password') }
                fullWidth={ true }
                size="large"
                // mark as required for styling when has content
                attrs={ { type: 'password', required: true, autoFocus: passwordAutofocus } }
                onEnter={ this.onSubmit }
              />
            </InputLabel>
            <CountryAndStatePicker
              country={ this.state.country }
              state={ this.state.state }
              onCountryChange={ this.setCountry }
              onStateChange={ this.setCountryState }
            />

            { RECAPTCHA_ENABLED && <Recaptcha ref={ this.recaptcha } /> }

            <div className={ styles.error }>
              { this.state.error &&
                <Alert theme="error" isCloseable={ true } onClose={ () => this.setState({ error: null }) }>
                  { this.state.error }
                </Alert>
              }
            </div>

            <Button
              className={ styles.buttonAction }
              fullWidth={ true }
              isLoading={ this.state.isLoading }
              size="large"
              loadingText={ getTranslation('common.label.verifying') }
              onClick={ this.onSubmit }
              disabled={true}
            >
              Currently Disabled
            </Button>

            <div className="my-4">
              <Subtitle type="small" color="dark-light">
                { t('onboarding.header.alreadyHave') }
                &nbsp;
                <Link className="font-weight--medium" to={ LOGIN }>
                  { t('common.action.login') }
                </Link>
              </Subtitle>
            </div>
          </div>
          <div className={ classnames('container mb-5 mb-md-0 pb-3 pb-md-2', styles.bottomText) }>
            <Caption color="gray" className="narrow--large">
              By clicking sign up, you have read and agree to the&nbsp;
              <Link to={ TERMS_OF_USE }>Terms of Use</Link>,&nbsp;
              <Link to={ PRIVACY_POLICY }>Privacy Policy</Link> and&nbsp;
              <Link to={ REFUND_POLICY }>Refund Policy</Link>.
            </Caption>
          </div>
        </div>
      </div>
    );
  }
}

export default Signup;
